import { useState, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Input,
  FormField,
  GstinInput,
  Alert,
  RadioGroup,
  Select,
  Toggle,
  Badge,
  ErrorBoundary,
  useToast,
  resolveTaxRegime,
} from '@bbpos/ui';
import { getCountryForCurrency, SUPPORTED_CURRENCIES } from '@bbpos/types';
import type { CurrencyCode } from '@bbpos/types';
import { tenantService } from '@/services/tenant.service';
import { subscriptionService } from '@/services/subscription.service';
import { enquiriesService } from '@/services/enquiries.service';
import { BusinessTypeSelector } from '@/components/features/tenants/BusinessTypeSelector';
import { COUNTRY_BY_CURRENCY } from '@/components/features/tenants/tenant-ui';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import type { BusinessType, SubscriptionPlan, CreateTenantPayload } from '@/types/tenant';

// ─── Design tokens ──────────────────────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

// ─── Validation ─────────────────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+[1-9]\d{6,14}$/;


const FEATURE_FLAGS = [
  { key: 'ENABLE_KDS', label: 'Kitchen Display System', description: 'Enable KDS for restaurant order routing' },
  { key: 'ENABLE_FESTIVE_MODULE', label: 'Festive Orders', description: 'Seasonal/festive order workflow' },
  { key: 'ENABLE_VOICE_POS', label: 'Voice POS', description: 'Voice-driven POS in regional languages' },
  { key: 'ENABLE_CREDIT_BILLING', label: 'Credit Billing', description: 'Credit-based billing for customers' },
  { key: 'ENABLE_DISPATCH', label: 'Dispatch Management', description: 'Delivery dispatch tracking' },
  { key: 'ENABLE_MATERIAL_INVENTORY', label: 'Material Inventory', description: 'Material/manufacturing inventory' },
];

const STEPS = ['Business', 'Owner', 'Plan', 'Review'] as const;

// ─── Country → currency ─────────────────────────────────────────────────────
const COUNTRY_OPTIONS = SUPPORTED_CURRENCIES.map((code) => ({
  value: code,
  label: `${COUNTRY_BY_CURRENCY[code]} (${code})`,
}));

// ─── Payload builders (exported for unit tests) ─────────────────────────────

export interface TenantCreateFormValues {
  businessName: string;
  businessType: BusinessType;
  currency: CurrencyCode;
  taxId: string;
  ownerName: string;
  ownerEmail: string;
  /** E.164 (the phone input adds the dial code); blank means none. */
  ownerPhone: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  plan: SubscriptionPlan;
  /** Blank means "inherit the plan limit". */
  maxUsers: string;
  maxLocations: string;
  maxRequestsPerMinute: string;
}

/**
 * Build the creation payload. Only the fields `CreateTenantDto` accepts —
 * the API runs `forbidNonWhitelisted`, so anything extra fails with 400.
 * The tax id is routed by regime: India → `gstin`, GCC → `tax_registration_number`.
 * The address is sent with the tenant itself so the Owner's POS Settings page is
 * pre-populated from the subscription data. `pincode` is India-only (the API
 * validates a 6-digit pattern).
 */
export function buildCreateTenantPayload(v: TenantCreateFormValues): CreateTenantPayload {
  const taxId = v.taxId.trim().toUpperCase();
  const payload: CreateTenantPayload = {
    business_name: v.businessName.trim(),
    business_type: v.businessType,
    default_currency: v.currency,
    owner_name: v.ownerName.trim(),
    owner_email: v.ownerEmail.trim(),
  };
  if (v.ownerPhone.trim()) payload.phone = v.ownerPhone.trim();
  if (taxId) {
    if (resolveTaxRegime(v.currency).regime === 'GST_INDIA') payload.gstin = taxId;
    else payload.tax_registration_number = taxId;
  }
  if (v.address.trim()) payload.address = v.address.trim();
  if (v.city.trim()) payload.city = v.city.trim();
  if (v.state.trim()) payload.state = v.state.trim();
  if (v.currency === 'INR' && v.pincode.trim()) payload.pincode = v.pincode.trim();
  // The plan supplies the tenant's default user/location ceilings; a blank
  // override box means "inherit the plan value", so it is simply omitted.
  if (v.plan) payload.subscription_plan = v.plan;
  const users = Number.parseInt(v.maxUsers, 10);
  if (Number.isFinite(users) && users > 0) payload.max_users = users;
  const locations = Number.parseInt(v.maxLocations, 10);
  if (Number.isFinite(locations) && locations > 0) payload.max_locations = locations;
  const rpm = Number.parseInt(v.maxRequestsPerMinute, 10);
  if (Number.isFinite(rpm) && rpm > 0) payload.max_requests_per_minute = rpm;
  return payload;
}

export interface BusinessStepValues {
  businessName: string;
  businessType?: BusinessType;
  currency: CurrencyCode;
  taxId: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
}

/**
 * Validate the Business step (step 1). The subscription address seeds the
 * tenant profile and the provisioned default location (#298), so address,
 * city and state are mandatory — plus a 6-digit pincode for India.
 */
export function validateBusinessStep(v: BusinessStepValues): Record<string, string> {
  const errs: Record<string, string> = {};
  const tax = resolveTaxRegime(v.currency);

  if (!v.businessName || v.businessName.length < 2) {
    errs.business_name = 'Business name is required (min 2 characters)';
  } else if (v.businessName.length > 200) {
    errs.business_name = 'Must be under 200 characters';
  }
  if (!v.businessType) {
    errs.business_type = 'Please select a business type';
  }
  if (v.taxId && tax.taxIdRegex && !tax.taxIdRegex.test(v.taxId)) {
    errs.gstin = `Invalid ${tax.taxIdLabel} format`;
  }
  if (!v.address.trim()) {
    errs.address = 'Address is required';
  }
  if (!v.city.trim()) {
    errs.city = 'City is required';
  }
  if (!v.state.trim()) {
    errs.state = 'State is required';
  }
  if (v.currency === 'INR') {
    if (!v.pincode.trim()) {
      errs.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(v.pincode.trim())) {
      errs.pincode = 'Pincode must be 6 digits';
    }
  }
  return errs;
}

// Server error codes → field mapping
const SERVER_ERROR_MAP: Record<string, { field: string; message: string }> = {
  // `message` is regime-driven at the call site (see createMutation.onError);
  // this neutral fallback is never shown for the GSTIN/TRN field.
  TENANT_GSTIN_DUPLICATE: { field: 'gstin', message: 'Tax ID already registered' },
  TENANT_NAME_DUPLICATE: { field: 'business_name', message: 'Business name already taken' },
  USER_EMAIL_DUPLICATE: { field: 'owner_email', message: 'Email already in use' },
};

/**
 * Server error → form field. The API envelope carries no `code`, so the code
 * map above only ever fires for a client that supplies one; a duplicate tax id
 * instead names its column in `details` (surfaced as `ApiError.details`).
 * India writes `gstin` and the GCC `tax_registration_number`, but the wizard
 * has a single input for both, keyed `gstin`. An owner email that already
 * belongs to another business (T06) comes back the same way, as `owner_email`.
 */
export function mapServerError(err: {
  code?: string;
  details?: { field: string; message: string }[];
}): { field: string; message: string } | null {
  const taxId = err.details?.find(
    (d) => d.field === 'gstin' || d.field === 'tax_registration_number',
  );
  if (taxId) return { field: 'gstin', message: taxId.message };
  const ownerEmail = err.details?.find((d) => d.field === 'owner_email');
  if (ownerEmail) return { field: 'owner_email', message: ownerEmail.message };
  return SERVER_ERROR_MAP[err.code ?? ''] ?? null;
}

// ─── Prefill from an enquiry (website plan §6.4) ────────────────────────────

/** Starting values, keyed by the wizard's own fields. */
export interface TenantCreatePrefill {
  businessName: string;
  businessType?: BusinessType;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  city: string;
  /** A plan `name` from the catalog. */
  plan?: SubscriptionPlan;
}

/**
 * Router state Platform → Enquiries sends with Create business. Router state,
 * not the query string, so no personal data goes in the URL.
 */
export interface TenantCreateRouterState {
  prefill: TenantCreatePrefill;
  enquiryId: string;
}

function readRouterState(state: unknown): TenantCreateRouterState | null {
  const s = state as Partial<TenantCreateRouterState> | null;
  return s && typeof s.enquiryId === 'string' && s.prefill ? (s as TenantCreateRouterState) : null;
}

// ─── Step indicator icons ───────────────────────────────────────────────────
function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="h-3.5 w-3.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function TenantCreateContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const formRef = useRef<HTMLDivElement>(null);

  // Read once: the enquiry only supplies the starting values.
  const location = useLocation();
  const [fromEnquiry] = useState(() => readRouterState(location.state));
  const prefill = fromEnquiry?.prefill;

  const [step, setStep] = useState(0);
  const [businessName, setBusinessName] = useState(prefill?.businessName ?? '');
  const [businessType, setBusinessType] = useState<BusinessType | undefined>(prefill?.businessType);
  const [currency, setCurrency] = useState<CurrencyCode>('INR');
  const [gstin, setGstin] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState(prefill?.city ?? '');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [ownerName, setOwnerName] = useState(prefill?.ownerName ?? '');
  const [ownerEmail, setOwnerEmail] = useState(prefill?.ownerEmail ?? '');
  const [ownerPhone, setOwnerPhone] = useState(prefill?.ownerPhone ?? '');
  const [plan, setPlan] = useState<SubscriptionPlan>(prefill?.plan ?? 'BASIC');
  const [maxUsers, setMaxUsers] = useState('');
  const [maxLocations, setMaxLocations] = useState('');
  const [maxRequestsPerMinute, setMaxRequestsPerMinute] = useState('');
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // The plan catalog is editable at runtime, so the ceilings shown next to each
  // plan (and used as the override placeholders) are read from it rather than
  // hardcoded. Public endpoint — safe to call before the tenant exists.
  const { data: plans } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => subscriptionService.listPlans(),
    staleTime: 5 * 60 * 1000,
  });

  const planOptions = useMemo(
    () =>
      (plans ?? []).map((p) => ({
        value: p.name,
        label: p.display_name,
        description: `${p.max_locations} location${p.max_locations === 1 ? '' : 's'} · ${p.max_users} users`,
      })),
    [plans],
  );

  const selectedPlan = useMemo(() => (plans ?? []).find((p) => p.name === plan), [plans, plan]);

  // Tax vocabulary (GST/VAT, GSTIN/TRN, validation pattern) follows the
  // selected country's currency — INR resolves to the India regime.
  const tax = resolveTaxRegime(currency);

  // Switching country invalidates a tax id entered under the previous regime,
  // and pincode only exists for India.
  function handleCurrencyChange(next: CurrencyCode) {
    setCurrency(next);
    setGstin('');
    if (next !== 'INR') setPincode('');
    setErrors((prev) => ({ ...prev, gstin: '', pincode: '' }));
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const tenant = await tenantService.create(
        buildCreateTenantPayload({
          businessName,
          businessType: businessType!,
          currency,
          taxId: gstin,
          ownerName,
          ownerEmail,
          ownerPhone,
          address,
          city,
          state,
          pincode,
          plan,
          maxUsers,
          maxLocations,
          maxRequestsPerMinute,
        }),
      );

      // Feature flags are a separate endpoint — a failure there must not lose
      // the created tenant (a MODERATOR cannot set feature flags).
      const warnings: string[] = [];
      const flags = Object.entries(features)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (flags.length > 0) {
        try {
          await tenantService.updateFeatureFlags(tenant.id, flags);
        } catch {
          warnings.push('feature flags');
        }
      }

      // Same rule: the business is created even if the enquiry is not updated.
      let enquiryNotUpdated = false;
      if (fromEnquiry) {
        try {
          await enquiriesService.update(fromEnquiry.enquiryId, {
            status: 'CONVERTED',
            converted_tenant_id: tenant.id,
          });
        } catch {
          enquiryNotUpdated = true;
        }
      }
      return { tenant, warnings, enquiryNotUpdated };
    },
    onSuccess: ({ tenant, warnings, enquiryNotUpdated }) => {
      toast({ title: 'Tenant created successfully', variant: 'success' });
      if (warnings.length > 0) {
        toast({
          title: `Could not apply ${warnings.join(' and ')}`,
          description: 'Set them from the tenant detail page.',
          variant: 'warning',
        });
      }
      if (enquiryNotUpdated) {
        toast({
          title: 'The enquiry was not updated',
          description: 'The business was created. Go to Platform → Enquiries and mark the enquiry Converted.',
          variant: 'warning',
        });
      }
      navigate(`/tenants/${tenant.id}`);
    },
    onError: (err: Error & { code?: string; details?: { field: string; message: string }[] }) => {
      const mapped = mapServerError(err);
      if (mapped) {
        const message =
          mapped.field === 'gstin' ? `${tax.taxIdLabel} already registered` : mapped.message;
        setErrors((prev) => ({ ...prev, [mapped.field]: message }));
        if (mapped.field === 'business_name' || mapped.field === 'gstin') setStep(0);
        if (mapped.field === 'owner_email') setStep(1);
      } else {
        toast({ title: err.message ?? 'Failed to create tenant', variant: 'error' });
      }
    },
  });

  function validateStep(s: number): boolean {
    const errs: Record<string, string> = {};

    if (s === 0) {
      Object.assign(
        errs,
        validateBusinessStep({
          businessName,
          businessType,
          currency,
          taxId: gstin,
          address,
          city,
          state,
          pincode,
        }),
      );
    }

    if (s === 1) {
      if (!ownerEmail) {
        errs.owner_email = 'Owner email is required';
      } else if (!EMAIL_RE.test(ownerEmail)) {
        errs.owner_email = 'Invalid email address';
      }
      if (!ownerName || ownerName.length < 2) {
        errs.owner_name = 'Owner name is required';
      }
      if (ownerPhone && !PHONE_RE.test(ownerPhone)) {
        errs.owner_phone = 'Enter a valid phone number';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleNext() {
    if (validateStep(step)) {
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    }
  }

  function handleBack() {
    setStep((s) => Math.max(s - 1, 0));
  }

  function handleSubmit() {
    createMutation.mutate();
  }

  // Ctrl+Enter to submit on review step
  useKeyboardShortcut('Enter', useCallback(() => {
    if (step === STEPS.length - 1) handleSubmit();
  },[step, businessName, businessType, ownerEmail, ownerName, plan]));

  return (
    <div className="max-w-2xl mx-auto animate-fade-in" ref={formRef}>
      {fromEnquiry && (
        <p className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
          <span className="h-1.5 w-1.5 rounded-full bg-violet-500" aria-hidden="true" />
          Filled in from an enquiry
        </p>
      )}

      {/* Step indicator — glass panel with colored states */}
      <div className={cn(GLASS_PANEL, 'p-4 mb-6')}>
        <div className="flex items-center justify-between">
          {STEPS.map((label, i) => {
            const isCompleted = i < step;
            const isCurrent = i === step;
            return (
              <div key={label} className="flex items-center gap-2">
                {i > 0 && (
                  <div className={cn(
                    'hidden sm:block h-px w-8 lg:w-16 transition-colors',
                    isCompleted ? 'bg-emerald-400' : 'bg-neutral-200 dark:bg-neutral-700',
                  )} />
                )}
                <button
                  type="button"
                  onClick={() => { if (i < step) setStep(i); }}
                  disabled={i > step}
                  className="flex items-center gap-2"
                >
                  <span className={cn(
                    'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-all',
                    isCompleted && 'bg-emerald-500 text-white',
                    isCurrent && 'bg-brand-600 text-white shadow-[0_0_12px_-4px_rgba(59,130,246,0.6)]',
                    !isCompleted && !isCurrent && 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400',
                  )}>
                    {isCompleted ? <CheckIcon /> : i + 1}
                  </span>
                  <span className={cn(
                    'hidden sm:block text-xs font-medium transition-colors',
                    isCurrent && 'text-brand-600 dark:text-brand-400',
                    isCompleted && 'text-emerald-600 dark:text-emerald-400',
                    !isCompleted && !isCurrent && 'text-neutral-400 dark:text-neutral-500',
                  )}>
                    {label}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step 1: Business Info (spec: name, type, GSTIN, address) */}
      {step === 0 && (
        <div className={cn(GLASS_PANEL, 'p-6 animate-slide-up')}>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-4">
            Business Information
          </h3>

          <Alert
            variant="warning"
            title="Business type and country are permanent"
            description="Neither can be changed after creation. Business type determines the inventory model, billing flow, and available features; country fixes the currency and tax regime (GST or VAT)."
          />

          <div className="space-y-4 mt-4">
            <FormField label="Business Name" htmlFor="business-name" required error={errors.business_name}>
              <Input
                id="business-name"
                placeholder="e.g., Sharma Electronics"
                value={businessName}
                onChange={setBusinessName}
              />
            </FormField>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Business Type <span className="text-danger-500">*</span>
              </label>
              <BusinessTypeSelector value={businessType} onChange={setBusinessType} />
              {errors.business_type && (
                <p className="mt-1 text-xs text-danger-500">{errors.business_type}</p>
              )}
            </div>

            <FormField label="Country / Currency" htmlFor="tenant-country" required>
              <Select
                id="tenant-country"
                options={COUNTRY_OPTIONS}
                value={currency}
                onChange={(v) => handleCurrencyChange(v as CurrencyCode)}
              />
              <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
                {tax.taxIdLabel
                  ? `Tax regime: ${tax.taxName} · tax ID: ${tax.taxIdLabel}`
                  : `Tax regime: no VAT · no statutory tax ID`}
              </p>
            </FormField>

            {/* Kuwait and Qatar have no statutory tax id — no field to show. */}
            {tax.taxIdLabel && (
              <GstinInput
                regime={tax}
                value={gstin}
                onChange={setGstin}
                error={errors.gstin}
              />
            )}

            {/* Address fields (spec 3.4) — required: they seed the tenant
                profile and the provisioned default location (#298). */}
            <FormField label="Address" htmlFor="address" required error={errors.address}>
              <Input
                id="address"
                placeholder="Street address, building, floor"
                value={address}
                onChange={setAddress}
              />
            </FormField>

            <div className={cn(
              'grid grid-cols-1 gap-4',
              currency === 'INR' ? 'sm:grid-cols-3' : 'sm:grid-cols-2',
            )}>
              <FormField label="City" htmlFor="city" required error={errors.city}>
                <Input id="city" placeholder="e.g., Mumbai" value={city} onChange={setCity} />
              </FormField>
              <FormField label="State" htmlFor="state" required error={errors.state}>
                <Input id="state" placeholder="e.g., Maharashtra" value={state} onChange={setState} />
              </FormField>
              {/* Pincode is India-only — the profile endpoint validates 6 digits. */}
              {currency === 'INR' && (
                <FormField label="Pincode" htmlFor="pincode" required error={errors.pincode}>
                  <Input id="pincode" placeholder="e.g., 400001" value={pincode} onChange={setPincode} />
                </FormField>
              )}
            </div>
          </div>

          <div className="flex justify-end mt-6 pt-4">
            <Button variant="primary" onClick={handleNext}>
              Next: Owner Details
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Owner Details */}
      {step === 1 && (
        <div className={cn(GLASS_PANEL, 'p-6 animate-slide-up')}>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-4">
            Owner Details
          </h3>

          <div className="space-y-4">
            <FormField label="Owner Name" htmlFor="owner-name" required error={errors.owner_name}>
              <Input
                id="owner-name"
                placeholder="e.g., Rajesh Sharma"
                value={ownerName}
                onChange={setOwnerName}
              />
            </FormField>

            <FormField label="Owner Email" htmlFor="owner-email" required error={errors.owner_email}>
              <Input
                id="owner-email"
                type="email"
                placeholder="owner@business.com"
                value={ownerEmail}
                onChange={setOwnerEmail}
              />
              <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
                OTP invite will be sent to this email after approval
              </p>
            </FormField>

            <FormField label="Phone" htmlFor="owner-phone" error={errors.owner_phone}>
              <Input
                id="owner-phone"
                variant="phone"
                // The country the user just picked in step 1, not the acting
                // admin's own tenant — this wizard is creating a new tenant.
                defaultDial={getCountryForCurrency(currency).dial}
                placeholder="98765 43210"
                value={ownerPhone}
                onChange={setOwnerPhone}
              />
            </FormField>
          </div>

          <div className="flex items-center justify-between mt-6 pt-4">
            <Button variant="ghost" onClick={handleBack}>Back</Button>
            <Button variant="primary" onClick={handleNext}>
              Next: Plan &amp; Features
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Plan & Features */}
      {step === 2 && (
        <div className={cn(GLASS_PANEL, 'p-6 animate-slide-up')}>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-4">
            Subscription Plan
          </h3>

          <RadioGroup
            options={planOptions}
            value={plan}
            onChange={(v: string) => setPlan(v as SubscriptionPlan)}
            variant="card"
          />

          <p className="mt-2 text-xs text-neutral-400 dark:text-neutral-500">
            The plan sets how many users and locations this tenant may create. It takes effect
            immediately and is billed once the tenant is approved and provisioned.
          </p>

          <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mt-8 mb-1">
            Custom Limits
          </h3>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-4">
            Leave blank to use the plan's limits. Set a number here to give this one tenant a
            different ceiling.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Max users" htmlFor="max-users">
              <Input
                id="max-users"
                type="number"
                min={1}
                value={maxUsers}
                onChange={setMaxUsers}
                placeholder={selectedPlan ? `Plan default: ${selectedPlan.max_users}` : 'Plan default'}
              />
            </FormField>

            <FormField label="Max locations" htmlFor="max-locations">
              <Input
                id="max-locations"
                type="number"
                min={1}
                value={maxLocations}
                onChange={setMaxLocations}
                placeholder={selectedPlan ? `Plan default: ${selectedPlan.max_locations}` : 'Plan default'}
              />
            </FormField>

            <FormField label="API requests per minute" htmlFor="max-rpm">
              <Input
                id="max-rpm"
                type="number"
                min={1}
                value={maxRequestsPerMinute}
                onChange={setMaxRequestsPerMinute}
                placeholder={
                  selectedPlan?.max_requests_per_minute
                    ? `Plan default: ${selectedPlan.max_requests_per_minute}`
                    : 'No limit'
                }
              />
            </FormField>
          </div>

          <p className="mt-3 text-xs text-neutral-400 dark:text-neutral-500">
            Provisioning creates the owner account and a &ldquo;Main Branch&rdquo; location, so both
            limits must be at least 1.
          </p>

          <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mt-8 mb-4">
            Feature Flags
          </h3>

          <div className="space-y-3">
            {FEATURE_FLAGS.map((ff) => (
              <Toggle
                key={ff.key}
                label={ff.label}
                description={ff.description}
                checked={features[ff.key] ?? false}
                onChange={(checked) =>
                  setFeatures((prev) => ({ ...prev, [ff.key]: checked }))
                }
              />
            ))}
          </div>

          <div className="flex items-center justify-between mt-6 pt-4">
            <Button variant="ghost" onClick={handleBack}>Back</Button>
            <Button variant="primary" onClick={handleNext}>
              Next: Review
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Review */}
      {step === 3 && (
        <div className={cn(GLASS_PANEL, 'p-6 animate-slide-up')}>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-4">
            Review &amp; Create
          </h3>

          <div className="space-y-4">
            {/* Business */}
            <div className="rounded-xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-3">
                Business
              </p>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-neutral-500 dark:text-neutral-400">Name</dt>
                  <dd className="font-medium text-neutral-900 dark:text-neutral-100">{businessName}</dd>
                </div>
                <div>
                  <dt className="text-neutral-500 dark:text-neutral-400">Type</dt>
                  <dd>
                    <Badge variant={businessType === 'RETAIL' ? 'info' : 'success'}>
                      {businessType}
                    </Badge>
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-neutral-500 dark:text-neutral-400">Country / Currency</dt>
                  <dd className="font-medium text-neutral-900 dark:text-neutral-100">
                    {COUNTRY_BY_CURRENCY[currency]} ({currency}) · {tax.taxName}
                  </dd>
                </div>
                {gstin && tax.taxIdLabel && (
                  <div className="col-span-2">
                    <dt className="text-neutral-500 dark:text-neutral-400">{tax.taxIdLabel}</dt>
                    <dd className="font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{gstin}</dd>
                  </div>
                )}
                {(address || city || state || pincode) && (
                  <div className="col-span-2">
                    <dt className="text-neutral-500 dark:text-neutral-400">Address</dt>
                    <dd className="text-neutral-900 dark:text-neutral-100">
                      {[address, city, state, pincode].filter(Boolean).join(', ')}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Owner */}
            <div className="rounded-xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-3">
                Owner
              </p>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-neutral-500 dark:text-neutral-400">Name</dt>
                  <dd className="font-medium text-neutral-900 dark:text-neutral-100">{ownerName}</dd>
                </div>
                <div>
                  <dt className="text-neutral-500 dark:text-neutral-400">Email</dt>
                  <dd className="text-neutral-900 dark:text-neutral-100">{ownerEmail}</dd>
                </div>
                {ownerPhone && (
                  <div>
                    <dt className="text-neutral-500 dark:text-neutral-400">Phone</dt>
                    <dd className="font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{ownerPhone}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Plan & Features */}
            <div className="rounded-xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-3">
                Plan &amp; Features
              </p>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge variant="info">{plan}</Badge>
                <Badge variant={maxUsers ? 'warning' : 'default'}>
                  {maxUsers || selectedPlan?.max_users || '—'} users
                </Badge>
                <Badge variant={maxLocations ? 'warning' : 'default'}>
                  {maxLocations || selectedPlan?.max_locations || '—'} locations
                </Badge>
                <Badge variant={maxRequestsPerMinute ? 'warning' : 'default'}>
                  {maxRequestsPerMinute || selectedPlan?.max_requests_per_minute || '∞'} req/min
                </Badge>
              </div>
              {Object.entries(features).filter(([, v]) => v).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {Object.entries(features)
                    .filter(([, v]) => v)
                    .map(([k]) => (
                      <Badge key={k} variant="info">{k.replace('ENABLE_', '').replaceAll('_', ' ')}</Badge>
                    ))}
                </div>
              )}
            </div>
          </div>

          <Alert
            variant="info"
            title="What happens next?"
            description="After creation, the tenant will be in PENDING_APPROVAL status. An Admin must approve and provision the tenant before the owner can log in."
            className="mt-4"
          />

          <div className="flex items-center justify-between mt-6 pt-4">
            <Button variant="ghost" onClick={handleBack}>Back</Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={createMutation.isPending}
            >
              Create Tenant
            </Button>
          </div>

          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center mt-2">
            Press Ctrl+Enter to submit
          </p>
        </div>
      )}
    </div>
  );
}

export function TenantCreatePage() {
  return (
    <PageContainer
      title="Create New Tenant"
      subtitle="Set up a new business on the platform"
      variant="compact"
      breadcrumbs={[{ label: 'Tenants', href: '/tenants' }, { label: 'New Tenant' }]}
    >
      <ErrorBoundary>
        <TenantCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
